import {IosServerName, OrderNumber} from "app/orders/models/OrderRecord";
import {
    EquityOrderViewModel,
    OptionOrderViewModel,
    OrderViewModel,
    TmcOrderLegViewModel,
    TmcOrderViewModel,
    WarrantOrderViewModel
} from "app/orders/models/OrderViewModel";
import {
    OptionQuoteViewModel,
    QuoteViewModel,
    TmcQuoteViewModel,
    UnderlyingQuoteViewModel
} from "app/quote/models/QuoteViewModel";
import {PriceStrategy} from "app/components/inputs/price/input/PriceStrategy";
import {OptionRecord} from "app/options/contracts/models/OptionChainRecord";
import {AccountBriefRecord} from "app/account/types/AccountBriefRecord";
import {IressOrderRecord, IressTmcOrderRecord} from "./IressOrderRecord";
import {OrderConditions} from "../OrderConditions";

export type IressOrderViewModels = ReadonlyArray<IressOrderViewModel>;

export interface IressOrderViewModel<Q extends QuoteViewModel = QuoteViewModel> extends OrderViewModel<Q> {
    readonly orderNumber: OrderNumber;
    readonly serverName: IosServerName;
}

export function isIressOrderModel(model: OrderViewModel): model is IressOrderViewModel {
    return "serverName" in model;
}

export interface ConditionalIressOrderViewModel<Q extends QuoteViewModel = QuoteViewModel> extends IressOrderViewModel<Q> {
    readonly conditions: OrderConditions;
}

export function isConditionalIressOrderViewModel(model: OrderViewModel): model is ConditionalIressOrderViewModel {
    return ("conditions" in model) && model["conditions"] != null;
}

export class IressOptionOrderViewModel extends OptionOrderViewModel implements IressOrderViewModel<OptionQuoteViewModel> {

    public readonly serverName: IosServerName;
    public readonly averagePrice: number;
    public readonly isInternal: boolean;

    public constructor(order: IressOrderRecord,
                       isAmendable: boolean,
                       quote: OptionQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       isInternal: boolean,
                       contract: OptionRecord | null) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, isInternal, contract);
        this.serverName = order.iosServer;
        this.averagePrice = order.averagePrice;
    }
}

export function isIressOptionOrderModel(model: IressOrderViewModel): model is IressOptionOrderViewModel {
    return model instanceof IressOptionOrderViewModel;
}

export class ConditionalIressOptionOrderViewModel extends IressOptionOrderViewModel implements ConditionalIressOrderViewModel<OptionQuoteViewModel> {

    public constructor(order: IressOrderRecord,
                       isAmendable: boolean,
                       quote: OptionQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       isInternal: boolean,
                       contract: OptionRecord | null,
                       public readonly conditions: OrderConditions) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, isInternal, contract);
    }
}

export function isConditionalIressOptionOrder(model: IressOrderViewModel): model is ConditionalIressOptionOrderViewModel {
    return isIressOptionOrderModel(model) && isConditionalIressOrderViewModel(model);
}

export class IressTmcOrderViewModel extends TmcOrderViewModel implements IressOrderViewModel<TmcQuoteViewModel> {

    public readonly serverName: IosServerName;
    public readonly averagePrice: number;
    public readonly isInternal: boolean;

    public constructor(order: IressTmcOrderRecord,
                       isAmendable: boolean,
                       quote: TmcQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       isInternal: boolean,
                       legs: ReadonlyArray<TmcOrderLegViewModel>) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, isInternal, legs);
        this.serverName = order.iosServer;
        this.averagePrice = order.averagePrice;
    }
}

export function isIressTmcOrderModel(model: IressOrderViewModel): model is IressTmcOrderViewModel {
    return model instanceof IressTmcOrderViewModel;
}

export class ConditionalIressTmcOrderViewModel extends IressTmcOrderViewModel implements ConditionalIressOrderViewModel<TmcQuoteViewModel> {

    public constructor(order: IressTmcOrderRecord,
                       isAmendable: boolean,
                       quote: TmcQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       isInternal: boolean,
                       legs: ReadonlyArray<TmcOrderLegViewModel>,
                       public readonly conditions: OrderConditions) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, isInternal, legs);
    }
}

export function isConditionalIressTmcOrderModel(model: IressOrderViewModel): model is ConditionalIressTmcOrderViewModel {
    return isIressTmcOrderModel(model) && isConditionalIressOrderViewModel(model);
}

export class IressEquityOrderViewModel extends EquityOrderViewModel implements IressOrderViewModel<UnderlyingQuoteViewModel> {

    public readonly serverName: IosServerName;
    public readonly averagePrice: number;

    public constructor(order: IressOrderRecord,
                       isAmendable: boolean,
                       quote: UnderlyingQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       isInternal: boolean) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, isInternal);
        this.serverName = order.iosServer;
        this.averagePrice = order.averagePrice;
    }
}

export function isIressEquityOrderModel(model: IressOrderViewModel): model is IressEquityOrderViewModel {
    return model instanceof IressEquityOrderViewModel;
}

export class ConditionalIressEquityOrderViewModel extends IressEquityOrderViewModel implements ConditionalIressOrderViewModel<UnderlyingQuoteViewModel> {

    public constructor(order: IressOrderRecord,
                       isAmendable: boolean,
                       quote: UnderlyingQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       isInternal: boolean,
                       public readonly conditions: OrderConditions) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, isInternal);
    }
}

export function isConditionalIressEquityOrderModel(model: IressOrderViewModel): model is ConditionalIressEquityOrderViewModel {
    return isIressEquityOrderModel(model) && isConditionalIressOrderViewModel(model);
}

export class IressWarrantOrderViewModel extends WarrantOrderViewModel implements IressOrderViewModel<UnderlyingQuoteViewModel> {

    public readonly serverName: IosServerName;
    public readonly averagePrice: number;

    public constructor(order: IressOrderRecord,
                       isAmendable: boolean,
                       quote: UnderlyingQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       isInternal: boolean) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, isInternal);
        this.serverName = order.iosServer;
        this.averagePrice = order.averagePrice;
    }
}

export function isIressWarrantOrderModel(model: IressOrderViewModel): model is IressWarrantOrderViewModel {
    return model instanceof IressWarrantOrderViewModel;
}

export class ConditionalIressWarrantOrderViewModel extends IressWarrantOrderViewModel implements ConditionalIressOrderViewModel<UnderlyingQuoteViewModel> {

    public constructor(order: IressOrderRecord,
                       isAmendable: boolean,
                       quote: UnderlyingQuoteViewModel | null,
                       priceStrategy: PriceStrategy,
                       account: AccountBriefRecord | null,
                       isMine: boolean,
                       isInternal: boolean,
                       public readonly conditions: OrderConditions) {
        super(order, isAmendable, quote, priceStrategy, account, isMine, isInternal);
    }
}

export function isConditionalIressWarrantOrderModel(model: IressOrderViewModel): model is ConditionalIressWarrantOrderViewModel {
    return isIressWarrantOrderModel(model) && isConditionalIressOrderViewModel(model);
}
